import { AuthContext, isTabletPortraitDown } from '@enter/core';
import { PageLayoutHeader, PageLayoutRow, PageLayoutTitle } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { RootStore } from 'stores';
import { Banking, Members, Overview, SecurityAndSafety } from './index';
import * as S from './settings.styles';
import { Api } from './api';
import { Webhooks } from './webhooks';
import { SingleSignOn } from './sso';
import { Hl7Settings } from './hl7';
import { SftpSettings } from './sftp';
import { Invoices } from './invoices';
import { CodingRules } from './coding-rules';

export const Settings = inject('rootStore')(
    observer((props: {}) => {
        const rootStore: RootStore = _.get(props, 'rootStore');
        if (!rootStore) {
            return <></>;
        }

        const { settingsStore } = rootStore;

        const authContext = useContext(AuthContext);
        const location = useLocation();
        const navigate = useNavigate();
        const { accountId } = useParams<{ accountId?: string }>();
        const [forceUpdate, setForceUpdate] = useState(0);

        const hasAdminAccess = rootStore.roleId === 'PROVIDER_ADMIN_ACCESS';

        // Redirect /settings to /settings/overview
        useEffect(() => {
            // Check if pathname ends with /settings or /settings/
            const pathname = location.pathname;
            if (pathname.endsWith('/settings') || pathname.endsWith('/settings/')) {
                navigate('overview', { replace: true });
            }
        }, [location.pathname, navigate]);

        useEffect(() => {
            document.title = 'ENTER Platform - Settings';
            authContext.setGroup('settings');

            let isMounted = true;

            // Initialize settings conditionally based on admin access
            const initPromises: Promise<void>[] = [
                settingsStore.fetchIntegrationApis(),
                settingsStore.fetchIntegrationHl7s(),
                settingsStore.fetchIntegrationSftp(),
                settingsStore.fetchUsers(),
                settingsStore.fetchInvitations(),
            ];

            // Fetch admin only resources if user has PROVIDER_ADMIN_ACCESS
            if (hasAdminAccess) {
                initPromises.push(settingsStore.fetchRoles());
            }

            Promise.all(initPromises)
                .then(() => {
                    if (isMounted) {
                        setForceUpdate(prev => prev + 1);
                    }
                })
                .catch(error => {
                    console.error('Failed to initialize settings:', error);
                });

            return () => {
                isMounted = false;
                authContext.cancelGroup('settings');
            };
        }, [authContext, settingsStore, hasAdminAccess]);

        const renderMobile = (): React.ReactElement => {
            return (
                <Routes>
                    <Route path="/" element={renderCategoryColumn()} />
                    <Route path="overview/*" element={<Overview key={`overview-${forceUpdate}`} />} />
                    <Route path="security-and-safety/*" element={<SecurityAndSafety />} />
                    <Route path="banking" element={<Banking />} />
                    {hasAdminAccess && <Route path="members" element={<Members />} />}
                    <Route path="api" element={<Api />} />
                    <Route path="webhooks" element={<Webhooks />} />
                    <Route path="invoices" element={<Invoices />} />
                    <Route path="coding-rules" element={<CodingRules />} />
                </Routes>
            );
        };

        // NB: visible only for desktop, or if mobile viewing '/settings'
        const renderCategoryColumn = (): React.ReactElement => {
            const settingsBase = accountId ? `/${accountId}/settings` : '/settings';
            return (
                <S.CategoryColumn>
                    <S.CategoryHeader>Account</S.CategoryHeader>
                    <S.Link to={`${settingsBase}/overview`}>
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Overview</S.CategoryItem>}
                    </S.Link>
                    <S.Link to={`${settingsBase}/security-and-safety`}>
                        {(props): React.ReactElement => (
                            <S.CategoryItem active={props.active}>Security & Safety</S.CategoryItem>
                        )}
                    </S.Link>
                    <S.Link to={`${settingsBase}/banking`}>
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Banking</S.CategoryItem>}
                    </S.Link>
                    <S.Link to={`${settingsBase}/invoices`}>
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Invoices</S.CategoryItem>}
                    </S.Link>
                    {hasAdminAccess && (
                        <>
                            <S.CategoryHeader>User & Team Management</S.CategoryHeader>
                            <S.Link to={`${settingsBase}/members`}>
                                {(props): React.ReactElement => (
                                    <S.CategoryItem active={props.active}>Members</S.CategoryItem>
                                )}
                            </S.Link>
                            <S.Link to={`${settingsBase}/sso`}>
                                {(props): React.ReactElement => (
                                    <S.CategoryItem active={props.active}>Single Sign-On</S.CategoryItem>
                                )}
                            </S.Link>
                        </>
                    )}
                    <S.CategoryHeader>Development</S.CategoryHeader>
                    <S.Link to={`${settingsBase}/api`}>
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>API Keys</S.CategoryItem>}
                    </S.Link>
                    <S.Link to={`${settingsBase}/hl7`}>
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>HL7</S.CategoryItem>}
                    </S.Link>
                    <S.Link to={`${settingsBase}/sftp`}>
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>SFTP</S.CategoryItem>}
                    </S.Link>
                    <S.Link to={`${settingsBase}/webhooks`}>
                        {(props): React.ReactElement => <S.CategoryItem active={props.active}>Webhooks</S.CategoryItem>}
                    </S.Link>
                    <S.Link to={`${settingsBase}/coding-rules`}>
                        {(props): React.ReactElement => (
                            <S.CategoryItem active={props.active}>Coding Rules</S.CategoryItem>
                        )}
                    </S.Link>
                </S.CategoryColumn>
            );
        };
        // NB: visible only for desktop, or if mobile viewing '/settings/[overview|security|banking|members]'
        const renderSubCategoryColumn = (): React.ReactElement => {
            return (
                <S.SubCategoryColumn>
                    <Routes>
                        <Route path="overview/*" element={<Overview key={`overview-${forceUpdate}`} />} />
                        <Route path="security-and-safety/*" element={<SecurityAndSafety />} />
                        <Route path="banking/*" element={<Banking />} />
                        {hasAdminAccess && <Route path="members/*" element={<Members />} />}
                        <Route path="api" element={<Api />} />
                        <Route path="webhooks" element={<Webhooks />} />
                        {hasAdminAccess && <Route path="sso" element={<SingleSignOn />} />}
                        <Route path="hl7" element={<Hl7Settings />} />
                        <Route path="sftp" element={<SftpSettings />} />
                        <Route path="invoices" element={<Invoices />} />
                        <Route path="coding-rules" element={<CodingRules />} />
                    </Routes>
                </S.SubCategoryColumn>
            );
        };

        return (
            <S.SettingsContainer>
                <S.SettingsHeader>
                    <PageLayoutRow>
                        <PageLayoutHeader>
                            <PageLayoutTitle>
                                <S.Link to={accountId ? `/${accountId}/settings` : '/settings'}>
                                    {(props): React.ReactElement => <span>Settings</span>}
                                </S.Link>
                            </PageLayoutTitle>
                        </PageLayoutHeader>
                    </PageLayoutRow>
                </S.SettingsHeader>
                <S.SettingsArea>
                    {isTabletPortraitDown() && renderMobile()}
                    {!isTabletPortraitDown() && renderCategoryColumn()}
                    {!isTabletPortraitDown() && renderSubCategoryColumn()}
                </S.SettingsArea>
            </S.SettingsContainer>
        );
    }),
);
